import { useState } from "react";
import { Check, CaretDown } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { CURRENCIES } from "@/lib/currencies";
import { currencySymbol } from "@/lib/format";
import { getRecentCurrencies, pushRecentCurrency } from "@/lib/recentCurrencies";
import { cn } from "@/lib/utils";

const NAME = Object.fromEntries(CURRENCIES);

// Type-to-search currency picker (code or name). Portal-rendered so it works
// inside modals. Recently-picked currencies are pinned to the top.
export function CurrencyCombobox({ value, onChange, className, testId = "currency" }) {
  const [open, setOpen] = useState(false);
  const label = value ? `${value}${NAME[value] ? ` — ${NAME[value]}` : ""}` : "Select currency";

  const recent = getRecentCurrencies().filter((c) => NAME[c]);
  const rest = CURRENCIES.filter(([c]) => !recent.includes(c));

  const pick = (code) => {
    pushRecentCurrency(code);
    onChange(code);
    setOpen(false);
  };

  const Row = ({ code }) => (
    <CommandItem
      key={code}
      value={`${code} ${NAME[code]}`}
      data-testid={`${testId}-combobox-option-${code}`}
      onSelect={() => pick(code)}
      className="flex items-center gap-2"
    >
      <span className="w-10 font-semibold tabular-nums text-riq-primary">{code}</span>
      <span className="flex-1 truncate text-neutral-600">{NAME[code]}</span>
      <span className="w-9 text-right text-xs text-muted-foreground">{currencySymbol(code).trim()}</span>
      <Check size={15} className={cn("text-riq-primary", value === code ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={`${testId}-combobox-trigger`}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-xl border border-riq-border bg-white px-3 py-2.5 text-sm text-left outline-none focus:ring-2 focus:ring-riq-primary/20",
            className,
          )}
        >
          <span className="truncate">{label}</span>
          <CaretDown size={15} className="opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 z-[200] w-[var(--radix-popover-trigger-width)] min-w-[260px]"
      >
        <Command>
          <CommandInput placeholder="Search currency…" data-testid={`${testId}-combobox-search`} />
          <CommandList>
            <CommandEmpty>No currency found.</CommandEmpty>
            {recent.length > 0 && (
              <CommandGroup heading="Recent">
                {recent.map((code) => <Row key={code} code={code} />)}
              </CommandGroup>
            )}
            <CommandGroup heading={recent.length > 0 ? "All currencies" : undefined}>
              {rest.map(([code]) => <Row key={code} code={code} />)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
